import React, { useState, useEffect } from 'react';
import { formatWithMethodDetectionLimitFrontend, getMethodDetectionLimit } from '../../config/methodDetectionLimits';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useTheme,
  alpha,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  TableContainer,
  Radio,
  RadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Snackbar,
  Checkbox,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Science,
  Analytics,
  CheckCircle,
  Assignment,
  Timer,
  PriorityHigh,
  TrendingUp,
  Assessment,
  FlashOn,
  Settings,
  Refresh,
  Save,
  PlayArrow,
  Stop,
  Warning,
  Info,
  ExpandMore,
  Tune,
  VerifiedUser,
  Group,
  Speed,
  Error,
  Cancel,
  Dashboard,
  People,
  ExpandLess,
  ExpandMore as ExpandMoreIcon,
  Visibility,
  Search,
  Clear,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import api from '../../services/api';

interface SampleWithAnalyst extends Sample {
  analyst_name?: string;
  isBatchValidation?: boolean;
  batchSamples?: SampleWithAnalyst[];
  current_laboratory?: string;
}

interface ValidationBatch {
  batch_id: string;
  analyst_name: string;
  sample_count: number;
  elements_analyzed: string[];
  analysis_date: string;
  avg_rsd: number;
  qc_status: 'PASSED' | 'WARNING' | 'FAILED';
  validation_priority: Priority;
  samples: Sample[];
}

interface MethodValidation {
  element: string;
  lod_ppm: number;
  loq_ppm: number;
  linear_range_min_ppm: number;
  linear_range_max_ppm: number;
  accuracy_percent: number;
  precision_rsd_percent: number;
  recovery_min_percent: number;
  recovery_max_percent: number;
  expanded_uncertainty_percent: number;
  validation_date: string;
}

interface AnalysisReading {
  element: string;
  intensity: number;
  concentration_ppm: number;
  concentration_final_percent: number;
  uncertainty: number;
  timestamp: string;
  instrument_settings: {
    wavelength: number;
    rf_power: number;
    nebulizer_flow: number;
    integration_time: number;
  };
}

interface ICPMethodConfig {
  method_id: string;
  method_name: string;
  elements: string[];
  detection_limits: Record<string, number>;
  calibration_standards: string[];
  qc_requirements: {
    max_rsd: number;
    required_blanks: number;
    required_duplicates: number;
    spike_recovery_range: [number, number];
  };
  instrument_conditions: Record<string, string>;
  is_active: boolean;
}

interface ICPPerformanceMetrics {
  total_samples_processed: number;
  average_turnaround_time: number;
  quality_pass_rate: number;
  instrument_uptime: number;
  analyst_efficiency: Record<string, number>;
  method_usage: Record<string, number>;
}

// 🔧 BGM ELEMENT ORDER: Standard order for BGM multi-lab samples (ICP elements first, Au at end)
// Au is placed at the END because it comes from AU lab (for comparison purposes)
const BGM_ELEMENT_ORDER = ['Ag', 'As', 'Cu', 'Fe', 'Mo', 'Pb', 'Sn', 'Zn', 'Au'];

// 🔧 Helper to sort elements in BGM order (for consistent display)
// Au is always placed at the END since it's from a different lab
const sortElementsInBGMOrder = (elements: string[]): string[] => {
  return elements.sort((a, b) => {
    // Au always goes to the END
    if (a === 'Au') return 1;
    if (b === 'Au') return -1;
    
    const aIndex = BGM_ELEMENT_ORDER.indexOf(a);
    const bIndex = BGM_ELEMENT_ORDER.indexOf(b);
    // If both are in BGM order, use that order
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    // If only one is in BGM order, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    // Otherwise, alphabetical
    return a.localeCompare(b);
  });
};

// 🔧 FIX: Get ALL unique elements across an entire batch (union of all samples)
// This ensures table headers show all possible elements even if samples have different selections
// 🆕 includeAuForBGM: For validation modal, include AU for BGM samples to compare with ICP values
const getAllBatchElements = (batchSamples: Sample[], includeAuForBGM: boolean = false): string[] => {
  const allElements = new Set<string>();
  const isBGMBatch = batchSamples.some(sample => 
    sample.metadonnees?.bgm_method_selected === true || 
    sample.metadonnees?.multi_lab_workflow === true
  );
  
  batchSamples.forEach(sample => {
    const sampleElements = getSampleElements(sample, includeAuForBGM);
    sampleElements.forEach(el => allElements.add(el));
  });
  
  // 🔧 FIX: For BGM batches, use BGM element order; otherwise use standard sorting
  if (isBGMBatch) {
    return sortElementsInBGMOrder(Array.from(allElements));
  }
  
  // Sort elements in a logical order (major first, then trace)
  const majorElements = ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti'];
  const result = Array.from(allElements).sort((a, b) => {
    const aIsMajor = majorElements.includes(a);
    const bIsMajor = majorElements.includes(b);
    if (aIsMajor && !bIsMajor) return -1;
    if (!aIsMajor && bIsMajor) return 1;
    return a.localeCompare(b);
  });
  return result;
};

// Helper function to get selected elements for a sample
// 🔥 ENHANCED: For multi-lab samples, only show elements routed to ICP lab
// 🔧 FIX: Must filter by BOTH selected_elements AND element_lab_routing
// 🆕 includeAuForBGM: For validation modal, include AU for BGM samples to compare with AU lab values
const getSampleElements = (sample: Sample, includeAuForBGM: boolean = false): string[] => {
  // 🔧 CRITICAL FIX: Start with selected_elements, then filter by routing
  // This ensures elements removed per-sample are properly greyed out
  const selectedElements = sample.metadonnees?.selected_elements || [];
  const elementRouting = sample.metadonnees?.element_lab_routing || {};
  const isMultiLab = sample.metadonnees?.multi_lab_workflow === true || 
                     sample.metadonnees?.bgm_method_selected === true ||
                     Object.keys(elementRouting).length > 0;
  const isBGM = sample.metadonnees?.bgm_method_selected === true || 
                sample.metadonnees?.multi_lab_workflow === true;
  
  if (isMultiLab && selectedElements.length > 0 && Object.keys(elementRouting).length > 0) {
    // Filter selected_elements to only those routed to ICP lab (or no routing = default to ICP)
    let icpElements = selectedElements.filter((element: string) => {
      const routing = elementRouting[element];
      return !routing || routing === 'LABORATOIRE_ICP';
    });
    
    // 🆕 BGM FIX: For validation modal, include AU for comparison even though it's routed to AU lab
    if (includeAuForBGM && isBGM && selectedElements.includes('Au') && !icpElements.includes('Au')) {
      icpElements = [...icpElements, 'Au'];
      console.log(`🔍 ICP Supervisor: Including AU for BGM comparison in ${sample.sample_id}`);
    }
    
    if (icpElements.length > 0) {
      // 🔧 FIX: Sort in BGM order for consistent display
      const sortedElements = sortElementsInBGMOrder(icpElements);
      console.log(`🔍 ICP Supervisor: Elements for ${sample.sample_id} (BGM order):`, sortedElements);
      return sortedElements;
    }
  }
  
  // 2. Direct selected_elements array (for non-multi-lab samples)
  if (selectedElements.length > 0) {
    return isBGM ? sortElementsInBGMOrder(selectedElements) : selectedElements;
  }
  
  // 3. From analyses_demandees (analysis methods requested)
  if (sample.metadonnees?.analyses_demandees && Array.isArray(sample.metadonnees.analyses_demandees)) {
    // Map ICP methods to their complete element lists
    const methodToElements: {[key: string]: string[]} = {
      'PRE.MO/ANA/082': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Ti', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ga', 'Hg', 'Li', 'Mo', 'Ni', 'Pb', 'Sb', 'Se', 'Sn', 'Sr', 'Tl', 'V', 'W', 'Zn', 'Zr'], // ICP Lingots (bare code)
      'PRE.MO/ANA/083': ['Ag', 'Al', 'As', 'Ba', 'Be', 'Bi', 'Ca', 'Cd', 'Co', 'Cr', 'Cu', 'Fe', 'Ga', 'Hg', 'K', 'Li', 'Mg', 'Mn', 'Mo', 'Na', 'Ni', 'P', 'Pb', 'S', 'Sb', 'Se', 'Si', 'Sn', 'Sr', 'Ti', 'Tl', 'V', 'W', 'Zn', 'Zr'], // ICP solutions usine mg/l (bare code)
      'PRE.MO/ANA/038': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'B', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ga', 'Ge', 'In', 'Li', 'Mo', 'Nb', 'Ni', 'Pb', 'Sb', 'Se', 'Sn', 'Sr', 'Ta', 'Th', 'Tl', 'V', 'W', 'Y', 'Zn', 'Zr'], // ICP 4-acide (bare code)
      'D4A-ICP- PRE.MO/ANA/038': [
        'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
        'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ga', 'Ge', 
        'In', 'Li', 'Mo', 'Nb', 'Ni', 'Pb', 'Rb', 'Sb', 'Sc', 'Sn', 'Sr', 
        'Ta', 'Te', 'Th', 'U', 'V', 'W', 'Y', 'Zr'
      ], // 55 Elements ICP
      'D4A-ICP- PRE.MO/ANA/037': [
        'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
        'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'
      ], // 40 Elements ICP
      'FP-ICP PRE.MO/ANA/007': [
        'Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 
        'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu'
      ], // 32 Elements ICP Fusion
      'ICP_MS': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'ICP_OES': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'ICP': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'MULTI_ELEMENT': ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'],
      'BGM-MULTI-LAB': ['Ag', 'As', 'Au', 'Cu', 'Fe', 'Mo', 'Pb', 'Sn', 'Zn'], // 🔧 FIX: Include Au for BGM (shown in validation modal)
    };
    
    const elements = new Set<string>();
    sample.metadonnees.analyses_demandees.forEach(method => {
      const methodElements = methodToElements[method] || ['Fe', 'Cu', 'Pb', 'Zn', 'Mn', 'Ni'];
      methodElements.forEach(el => elements.add(el));
    });
    
    if (elements.size > 0) {
      const elementArray = Array.from(elements);
      return isBGM ? sortElementsInBGMOrder(elementArray) : elementArray;
    }
  }
  
  // 4. Default ICP elements if nothing else found (use common elements)
  return ['Al', 'Ca', 'Fe', 'K', 'Mg', 'Mn', 'Na', 'P', 'S', 'Si', 'Ti', 'Ag', 'As', 'Ba', 'Be', 'Bi', 'Cd', 'Co', 'Cr', 'Cu', 'Ni', 'Pb', 'Zn'];
};

const ICPSupervisorDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [samples, setSamples] = useState<SampleWithAnalyst[]>([]);
  const [loading, setLoading] = useState(true);
  const [validationDialog, setValidationDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState<SampleWithAnalyst | null>(null);
  const [validationDecision, setValidationDecision] = useState<'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS'>('APPROVE');
  const [validationNotes, setValidationNotes] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [methodValidations, setMethodValidations] = useState<MethodValidation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState({
    total_samples_processed: 0,
    average_turnaround_time: 0,
    quality_pass_rate: 98.5,
    instrument_uptime: 95,
    analyst_efficiency: {} as Record<string, number>,
    method_usage: {} as Record<string, number>,
  });
  const [validationBatches, setValidationBatches] = useState<ValidationBatch[]>([]);
  const [methodConfigDialog, setMethodConfigDialog] = useState(false);

  // 🚀 PAGINATION & SEARCH STATE
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(250); // ~10 batches worth
  const [totalSamples, setTotalSamples] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [batchCount, setBatchCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // QA/QC Data State (for BGM samples only)
  interface QAQCStandard {
    customers_ref: string;  // Row label (e.g., "BMX25120250")
    lab_ref: string;        // Editable (e.g., "R-9348-25-25")
    // 10 element columns - ALL EDITABLE (even if gray in template)
    Ag: string;
    Cu: string;
    Fe: string;
    Pb: string;
    Zn: string;
    Ag_FA: string;
    Au_FA: string;
    Mo_ICP: string;
    As_ICP: string;
    Sn_ICP: string;
  }
  const [qaqcStandards, setQaqcStandards] = useState<QAQCStandard[]>([]);
  const [showQaqcForm, setShowQaqcForm] = useState(false);
  
  // BIS Sample Selection State (2 BIS samples only)
  const [firstBISSampleId, setFirstBISSampleId] = useState<string | null>(null);
  const [secondBISSampleId, setSecondBISSampleId] = useState<string | null>(null);

  // 🆕 SUPERVISOR EDIT STATE: Track edited values for audit trail
  interface EditedValue {
    sampleId: string;
    element: string;
    originalValue: number;
    editedValue: number;
    timestamp: string;
  }
  const [editedValues, setEditedValues] = useState<Record<string, Record<string, number>>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, Record<string, number>>>({});
  const [editHistory, setEditHistory] = useState<EditedValue[]>([]);

  const [stats, setStats] = useState({
    pending_validation: 0,
    validated_today: 0,
    rejected_today: 0,
    total_in_lab: 0,
    instrument_uptime: 95,
    quality_pass_rate: 98.5,
    overdue_validation: 0,
    quality_issues: 0,
  });

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => {
      // ⏸️ PAUSE auto-refresh when validation modal is open to avoid interrupting user
      if (!validationDialog) {
        loadDashboardData();
      }
    }, 30000); // 30 seconds to reduce churn
    return () => clearInterval(interval);
  }, [validationDialog]); // Re-create interval when modal state changes

  // Debounce search input (800ms delay - gives time to finish typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data when page, rowsPerPage, or search changes
  useEffect(() => {
    loadValidationSamples();
  }, [page, rowsPerPage, debouncedSearch]);

  // Real-time updates for validation list
  useEffect(() => {
    const validationInterval = setInterval(() => {
      // ⏸️ PAUSE auto-refresh when validation modal is open to avoid interrupting user
      if (currentTab === 2 && !validationDialog) { // Only poll when on validation tab AND modal is closed
        loadValidationSamples();
      }
    }, 15000); // 15 seconds to reduce churn

    return () => clearInterval(validationInterval);
  }, [currentTab, validationDialog]); // Re-create interval when modal state changes

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadValidationSamples(),
        loadMethodValidations(),
        loadStats()
      ]);
    } catch (error) {
      // Silenced in production to avoid noisy logs
    } finally {
      setLoading(false);
    }
  };

  const loadValidationSamples = async () => {
    try {
      // 🚀 OPTIMIZED: Use new paginated API endpoint with BGM filtering on backend
      const response = await api.get<{
        samples: SampleWithAnalyst[];
        total: number;
        page: number;
        totalPages: number;
        batchCount: number;
      }>('/samples/queues/icp-validation', {
        params: {
          page: page + 1, // API is 1-indexed, MUI is 0-indexed
          limit: rowsPerPage,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          sortBy: 'date',
          sortOrder: 'desc',
        }
      });
      
      const icpSamples = (response.samples || []) as SampleWithAnalyst[];
      
      setSamples(icpSamples);
      setTotalSamples(response.total || 0);
      setTotalPages(response.totalPages || 0);
      setBatchCount(response.batchCount || 0);
      
    } catch (error) {
      // Silenced in production
      setSamples([]);
    }
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const loadMethodValidations = async () => {
    try {
      // ICP method validation data with real detection limits
      const validations: MethodValidation[] = [
        {
          element: 'Fe',
          lod_ppm: 0.010,
          loq_ppm: 0.030,
          linear_range_min_ppm: 0.05,
          linear_range_max_ppm: 1000,
          accuracy_percent: 98.2,
          precision_rsd_percent: 2.1,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 4.0,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Cu',
          lod_ppm: 0.003,
          loq_ppm: 0.010,
          linear_range_min_ppm: 0.02,
          linear_range_max_ppm: 500,
          accuracy_percent: 99.1,
          precision_rsd_percent: 1.8,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 3.5,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Pb',
          lod_ppm: 0.005,
          loq_ppm: 0.015,
          linear_range_min_ppm: 0.03,
          linear_range_max_ppm: 200,
          accuracy_percent: 97.8,
          precision_rsd_percent: 2.5,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 4.2,
          validation_date: new Date().toISOString()
        },
        {
          element: 'Zn',
          lod_ppm: 0.002,
          loq_ppm: 0.006,
          linear_range_min_ppm: 0.01,
          linear_range_max_ppm: 100,
          accuracy_percent: 98.7,
          precision_rsd_percent: 1.9,
          recovery_min_percent: 90,
          recovery_max_percent: 110,
          expanded_uncertainty_percent: 3.8,
          validation_date: new Date().toISOString()
        }
      ];
      setMethodValidations(validations);
    } catch (error) {
      // Silenced in production
      setMethodValidations([]);
    }
  };

  const loadStats = async () => {
    try {
      // Calculate stats from samples
      const pendingValidation = samples.filter(s => s.statut === 'VALIDATION_SERVICE').length;
      const validatedToday = samples.filter(s => {
        const completedAt = s.metadonnees?.validation_completed_at;
        return completedAt && new Date(completedAt as string).toDateString() === new Date().toDateString();
      }).length;
      
      setStats({
        pending_validation: pendingValidation,
        validated_today: validatedToday,
        rejected_today: 0,
        total_in_lab: samples.length,
        instrument_uptime: 95,
        quality_pass_rate: 98.5,
        overdue_validation: 0,
        quality_issues: 0,
      });
    } catch (error) {
      // Silenced in production
    }
  };

  const groupSamplesIntoBatches = (samples: Sample[]): ValidationBatch[] => {
    const batchMap = new Map<string, Sample[]>();
    
    samples.forEach(sample => {
      // Check both individual analysis data and batch analysis form
      const analysisData = sample.metadonnees?.icp_analysis_data || sample.metadonnees?.icp_analysis_form;
      const analystName = sample.metadonnees?.icp_analyst_name || analysisData?.analyst_name || 'Unknown';
      const completedAt = sample.metadonnees?.icp_analysis_completed_at || analysisData?.analysis_completed_at || sample.date_reception;
      
      if (analysisData || sample.metadonnees?.batch_analysis_summary) {
        const batchKey = `${analystName}-${new Date(completedAt).toDateString()}`;
        
        if (!batchMap.has(batchKey)) {
          batchMap.set(batchKey, []);
        }
        batchMap.get(batchKey)?.push(sample);
      }
    });
    
    return Array.from(batchMap.entries()).map(([key, batchSamples]) => {
      const [analystName, dateStr] = key.split('-');
      
      // 🔧 FIX: Use same data source priority as certificate service for consistency
      // Priority: analysis_results -> icp_analysis_form -> icp_analysis_data
      const allResults = batchSamples.flatMap(s => {
        const icpAnalysisSources = [
          s.metadonnees?.analysis_results,
          s.metadonnees?.icp_analysis_form?.analysis_readings,
          s.metadonnees?.icp_analysis_data?.analysis_readings
        ];
        
        for (const source of icpAnalysisSources) {
          if (source && Array.isArray(source) && source.length > 0) {
            return source;
          }
        }
        return [];
      });
      
      const elements = [...new Set(allResults.map((r: any) => r.element))];
      const avgRsd = allResults.length > 0 ? 
        allResults.reduce((sum: number, r: any) => sum + (r.uncertainty || r.rsd_percent || 0), 0) / allResults.length : 0;
      
      // Determine QC status based on available data
      const qcStatus: 'PASSED' | 'WARNING' | 'FAILED' = 
        allResults.every((r: any) => (r.quality_flag === 'PASS' || r.detection_status === 'VALID')) ? 'PASSED' :
        allResults.some((r: any) => (r.quality_flag === 'FAIL' || r.detection_status === 'BELOW_LOD')) ? 'FAILED' : 'WARNING';
      
      const priority = batchSamples.some(s => s.priorite === Priority.URGENTE) ? Priority.URGENTE :
                     batchSamples.some(s => s.priorite === Priority.ELEVEE) ? Priority.ELEVEE : Priority.NORMALE;
      
      return {
        batch_id: `ICP-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        analyst_name: analystName || 'Analyst Inconnu',
        sample_count: batchSamples.length,
        elements_analyzed: elements,
        analysis_date: dateStr,
        avg_rsd: avgRsd,
        qc_status: qcStatus,
        validation_priority: priority,
        samples: batchSamples,
      };
    }).sort((a, b) => {
      // Sort by priority (urgent first) then by date
      if (a.validation_priority !== b.validation_priority) {
        return a.validation_priority === Priority.URGENTE ? -1 : 1;
      }
      return new Date(b.analysis_date).getTime() - new Date(a.analysis_date).getTime();
    });
  };

  const calculatePerformanceMetrics = (samples: Sample[]) => {
    const totalProcessed = samples.length;
    
    // Calculate average turnaround time
    const turnaroundTimes = samples.map(sample => {
      const startTime = new Date(sample.date_reception).getTime();
      const analysisCompletedAt = sample.metadonnees?.icp_analysis_data?.analysis_completed_at;
      const endTime = analysisCompletedAt && typeof analysisCompletedAt === 'string' ? 
        new Date(analysisCompletedAt).getTime() : Date.now();
      return (endTime - startTime) / (1000 * 60 * 60); // hours
    });
    
    const avgTurnaround = turnaroundTimes.reduce((sum, time) => sum + time, 0) / turnaroundTimes.length;
    
    // Calculate quality pass rate
    const qualityResults = samples.flatMap(s => s.metadonnees?.icp_analysis_data?.analysis_results || []);
    const passRate = qualityResults.length > 0 ? 
      (qualityResults.filter(r => r.quality_flag === 'PASS').length / qualityResults.length) * 100 : 0;
    
    // Analyst efficiency
    const analystEfficiency: Record<string, number> = {};
    samples.forEach(sample => {
      const analyst = sample.metadonnees?.icp_analysis_data?.analyst_name;
      if (analyst) {
        analystEfficiency[analyst] = (analystEfficiency[analyst] || 0) + 1;
      }
    });
    
    setPerformanceMetrics({
      total_samples_processed: totalProcessed,
      average_turnaround_time: avgTurnaround,
      quality_pass_rate: passRate,
      instrument_uptime: 95, // Mock data - would come from instrument
      analyst_efficiency: analystEfficiency,
      method_usage: { 'ICP-55': 65, 'ICP-31': 35 }, // Mock data
    });
  };

  const handleValidateSample = (sample: SampleWithAnalyst) => {
    setSelectedSample(sample);
    setValidationDialog(true);
    setValidationDecision('APPROVE');
    setValidationNotes('');
  };

  // Load default QA/QC template - 6 rows, 12 columns (Customers-Ref, Lab-Ref, + 10 elements)
  const loadDefaultQAQCTemplate = () => {
    const defaultStandards: QAQCStandard[] = [
      {
        customers_ref: "BMX25120250",
        lab_ref: "",
        Ag: "",      // MO/ANA/002 | 1
        Cu: "",      // MO/ANA/002 | 0.01
        Fe: "",      // MO/ANA/002 | 0.01
        Pb: "",      // MO/ANA/002 | 0.01
        Zn: "",      // MO/ANA/002 | 0.01
        Ag_FA: "",   // MO/ANA/035 | 200 (grayed in template but MUST be editable)
        Au_FA: "",   // MO/ANA/001 | 0.05
        Mo_ICP: "",  // MO/ANA/038 | 1
        As_ICP: "",  // MO/ANA/038 | 1
        Sn_ICP: "",  // MO/ANA/038 | 1
      },
      {
        customers_ref: "BIS(BMX25120250)",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",   // Grayed but EDITABLE
        Au_FA: "",
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      },
      {
        customers_ref: "BLANK (mechanical preparation)",
        lab_ref: "",
        Ag: "1",     // Fixed value from QA/QC standard
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "<0.05",  // Fixed value from QA/QC standard
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      },
      {
        customers_ref: "BLANK (chemical preparation)",
        lab_ref: "",
        Ag: "1",     // Fixed value from QA/QC standard
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "<0.05",  // Fixed value from QA/QC standard
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      },
      {
        customers_ref: "OREAS 139",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",   // Grayed but EDITABLE
        Au_FA: "",   // Grayed but EDITABLE
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      },
      {
        customers_ref: "OREAS 603b",
        lab_ref: "",
        Ag: "",      // Grayed but EDITABLE
        Cu: "",      // Grayed but EDITABLE
        Fe: "",      // Grayed but EDITABLE
        Pb: "",      // Grayed but EDITABLE
        Zn: "",      // Grayed but EDITABLE
        Ag_FA: "",   // Grayed but EDITABLE
        Au_FA: "",
        Mo_ICP: "",  // Grayed but EDITABLE
        As_ICP: "",  // Grayed but EDITABLE
        Sn_ICP: "",  // Grayed but EDITABLE
      },
    ];
    
    setQaqcStandards(defaultStandards);
    setShowQaqcForm(true);
  };

  // Auto-fill BIS sample data (Row 1 or Row 2)
  const autoFillBISRow = (sampleId: string, rowIndex: number, useBISFormat: boolean = false) => {
    if (!selectedSample) return;
    
    const batchSamples = (selectedSample as any)?.batchSamples || [];
    const sample = batchSamples.find((s: any) => s.id === sampleId);
    
    if (!sample) {
      setSnackbarMessage('❌ Échantillon non trouvé');
      setSnackbarOpen(true);
      return;
    }
    
    const analysisResults = sample.metadonnees?.analysis_results || [];
    const icpAnalysisReadings = sample.metadonnees?.icp_analysis_form?.analysis_readings || [];
    const auAnalysisReadings = sample.metadonnees?.au_analysis_form?.analysis_readings || [];
    
    if (analysisResults.length === 0) {
      setSnackbarMessage(`❌ Pas de résultats d'analyse pour ${sample.sample_id}`);
      setSnackbarOpen(true);
      return;
    }
    
    // Get value with FIXED QA/QC detection limit handling
    const getValue = (element: string): string => {
      let result = analysisResults.find((r: any) => r.element === element);
      
      // Fixed QA/QC detection limits (from the table header)
      const qaqcDetectionLimits: Record<string, number> = {
        'Ag': 1,
        'Cu': 0.01,
        'Fe': 0.01,
        'Pb': 0.01,
        'Zn': 0.01,
        'Ag_FA': 200,
        'Au_FA': 0.05,
        'Mo_ICP': 1,
        'As_ICP': 1,
        'Sn_ICP': 1
      };
      
      // Map element names to QA/QC table column names
      const elementMapping: Record<string, string> = {
        'Ag': 'Ag',
        'Cu': 'Cu',
        'Fe': 'Fe',
        'Pb': 'Pb',
        'Zn': 'Zn',
        'Au': 'Au_FA',
        'Mo': 'Mo_ICP',
        'As': 'As_ICP',
        'Sn': 'Sn_ICP'
      };
      
      const qaqcColumnName = elementMapping[element] || element;
      const detectionLimit = qaqcDetectionLimits[qaqcColumnName] || 0.01;
      
      // Special handling for Au: check au_analysis_form if not found or = 0
      if (element === 'Au' && (!result || result.concentration_ppm === 0) && auAnalysisReadings.length > 0) {
        const auFormData = auAnalysisReadings.find((r: any) => r.element === 'Au');
        if (auFormData) {
          const auValue = auFormData.ppm || auFormData.grams_per_tonne || 0;
          // If Au value is below detection limit (0.05), show <0.05
          if (auValue < detectionLimit) {
            return `<${detectionLimit}`;
          }
          result = {
            element: 'Au',
            concentration_ppm: auValue,
            detection_status: 'VALID'
          };
        }
      }
      
      if (!result) return '';
      
      // If below detection limit, show "<detection_limit"
      if (result.detection_status === 'BELOW_LOD') {
        return `<${detectionLimit}`;
      }
      
      const ppm = result.concentration_ppm || 0;
      
      // If value is 0 or below detection limit, show <detection_limit
      if (ppm === 0 || ppm < detectionLimit) {
        return `<${detectionLimit}`;
      }
      
      // Round Ag, Mo, As, Sn to integers (for all samples)
      if (['Ag', 'Mo', 'As', 'Sn', 'Mo_ICP', 'As_ICP', 'Sn_ICP'].includes(element)) {
        return Math.round(ppm).toString();
      }
      
      return ppm.toFixed(2);
    };
    
    // Get client reference
    const clientRef = sample.external_id || sample.sample_id;
    
    // For 2nd BIS, use BIS(first BIS's external_id)
    let customersRefValue = clientRef;
    if (useBISFormat && firstBISSampleId) {
      const firstBISSample = batchSamples.find((s: any) => s.id === firstBISSampleId);
      const firstBISClientRef = firstBISSample?.external_id || firstBISSample?.sample_id || clientRef;
      customersRefValue = `BIS(${firstBISClientRef})`;
    }
    
    // Update the row
    const updatedStandards = [...qaqcStandards];
    updatedStandards[rowIndex] = {
      ...updatedStandards[rowIndex],
      customers_ref: customersRefValue,
      lab_ref: sample.sample_id,
      Ag: getValue('Ag'),
      Cu: getValue('Cu'),
      Fe: getValue('Fe'),
      Pb: getValue('Pb'),
      Zn: getValue('Zn'),
      Ag_FA: '',  // Keep empty (grayed column in QA/QC table)
      Au_FA: getValue('Au'),
      Mo_ICP: getValue('Mo'),
      As_ICP: getValue('As'),
      Sn_ICP: getValue('Sn')
    };
    
    setQaqcStandards(updatedStandards);
    setSnackbarMessage(`✅ Données importées depuis ${sample.sample_id} (${clientRef})`);
    setSnackbarOpen(true);
  };
  
  
  // Handle first BIS checkbox (fills Row 1 with sample's own external_id)
  const handleFirstBISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      // Uncheck second BIS if same sample
      if (sampleId === secondBISSampleId) {
        setSecondBISSampleId(null);
      }
      setFirstBISSampleId(sampleId);
      autoFillBISRow(sampleId, 0, false); // Row 0, no BIS format (use sample's own external_id)
    } else {
      setFirstBISSampleId(null);
      // Clear Row 1
      const updatedStandards = [...qaqcStandards];
      updatedStandards[0] = {
        customers_ref: "BMX25120250",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "",
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      };
      setQaqcStandards(updatedStandards);
    }
  };
  
  // Handle second BIS checkbox (fills Row 2 with BIS(first BIS's external_id))
  const handleSecondBISChange = (sampleId: string, checked: boolean) => {
    if (checked) {
      // Uncheck first BIS if same sample
      if (sampleId === firstBISSampleId) {
        setFirstBISSampleId(null);
        // Clear Row 1
        const updatedStandards = [...qaqcStandards];
        updatedStandards[0] = {
          customers_ref: "BMX25120250",
          lab_ref: "",
          Ag: "",
          Cu: "",
          Fe: "",
          Pb: "",
          Zn: "",
          Ag_FA: "",
          Au_FA: "",
          Mo_ICP: "",
          As_ICP: "",
          Sn_ICP: "",
        };
        setQaqcStandards(updatedStandards);
      }
      setSecondBISSampleId(sampleId);
      autoFillBISRow(sampleId, 1, true); // Row 1, use BIS format with first BIS's external_id
    } else {
      setSecondBISSampleId(null);
      // Clear Row 2
      const updatedStandards = [...qaqcStandards];
      updatedStandards[1] = {
        customers_ref: "BIS(BMX25120250)",
        lab_ref: "",
        Ag: "",
        Cu: "",
        Fe: "",
        Pb: "",
        Zn: "",
        Ag_FA: "",
        Au_FA: "",
        Mo_ICP: "",
        As_ICP: "",
        Sn_ICP: "",
      };
      setQaqcStandards(updatedStandards);
    }
  };

  // 🆕 SUPERVISOR EDIT: Handle value edits with audit trail
  const handleValueEdit = (sampleId: string, element: string, newValue: number, originalPpm: number) => {
    // Validate input
    if (isNaN(newValue) || newValue < 0) {
      console.warn(`Invalid value for ${element}: ${newValue}`);
      return;
    }

    // Store original value if first edit
    if (!originalValues[sampleId]?.[element]) {
      setOriginalValues(prev => ({
        ...prev,
        [sampleId]: {
          ...prev[sampleId],
          [element]: originalPpm
        }
      }));
    }

    // Update edited values
    setEditedValues(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [element]: newValue
      }
    }));

    // Track in edit history
    setEditHistory(prev => [...prev, {
      sampleId,
      element,
      originalValue: originalValues[sampleId]?.[element] || originalPpm,
      editedValue: newValue,
      timestamp: new Date().toISOString()
    }]);

    console.log(`✏️ Edit: ${element} for ${sampleId}: ${originalPpm} → ${newValue}`);
  };

  // 🆕 SUPERVISOR EDIT: Clear all edits (on dialog close or cancel)
  const clearAllEdits = () => {
    setEditedValues({});
    setOriginalValues({});
    setEditHistory([]);
  };

  const handleCompleteValidation = async () => {
    if (!selectedSample || !validationNotes.trim()) return;

    try {
      const token = localStorage.getItem('access_token');
      
      // Check if token exists
      if (!token) {
        setSnackbarMessage('❌ Session expirée - Veuillez vous reconnecter');
        setSnackbarOpen(true);
        return;
      }
      const isBatchValidation = (selectedSample as any)?.isBatchValidation;
      const batchSamples = (selectedSample as any)?.batchSamples || [selectedSample];
      
      if (isBatchValidation) {
        // Batch validation - validate all samples in the batch
        // Debug log removed
        
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];
        
        // 🚀 BATCH OPTIMIZATION: Concurrent validation calls instead of sequential
        console.log(`🔄 Batch validating ${batchSamples.length} ICP samples concurrently...`);
        
        const validationPromises = batchSamples.map(sample => {
          // 🆕 SUPERVISOR EDITS: Prepare edited values for this sample
          const sampleEdits = editedValues[sample.id];
          const supervisorEdits = sampleEdits ? Object.entries(sampleEdits).map(([element, editedValue]) => {
            const originalValue = originalValues[sample.id]?.[element] || 0;
            return {
              element,
              original_value: originalValue,
              edited_value: editedValue,
              edited_by: user?.id || '',
              edited_by_name: `${user?.firstName || ''} ${user?.lastName || ''}`,
              edited_at: new Date().toISOString(),
              edit_reason: validationNotes,
              change_magnitude: Math.abs(editedValue - originalValue),
              change_percentage: originalValue > 0 ? ((editedValue - originalValue) / originalValue) * 100 : 0
            };
          }) : [];

          return fetch(`/api/v1/samples/${sample.id}/validate`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              decision: validationDecision,
              notes: `${validationNotes} [Validation de lot ICP: ${getBatchNumber(selectedSample)}]`,
              validated_by: 'ICP Supervisor',
              batch_validation: true,
              batch_number: getBatchNumber(selectedSample),
              // Add QA/QC data if present (BGM samples only)
              ...(showQaqcForm && qaqcStandards.length > 0 && {
                qaqc_data: {
                  reference_standards: qaqcStandards,
                  batch_info: {
                    analyst_name: batchSamples[0]?.metadonnees?.icp_analyst_name || 'N/A',
                    analysis_date: new Date().toISOString(),
                    batch_number: getBatchNumber(selectedSample) || 'N/A',
                    sample_count: batchSamples.length
                  }
                }
              }),
              // 🆕 SUPERVISOR EDITS: Include edited values with full audit trail
              ...(supervisorEdits.length > 0 && {
                supervisor_edits: {
                  icp: supervisorEdits,
                  validation_session_id: `validation_${Date.now()}`,
                  total_edits: supervisorEdits.length
                }
              })
            })
          }).then(response => ({ sample, response }))
          .catch(error => ({ sample, error }));
        });

        // Wait for all validation calls to complete
        const results = await Promise.all(validationPromises);
        
        // Process results
        for (const result of results) {
          const { sample, response, error } = result;
          
          if (error) {
            errorCount++;
            errors.push(`${sample.sample_id}: Erreur de connexion`);
            console.warn(`❌ Validation error for ${sample.sample_id}:`, error);
          } else if (response.ok) {
            successCount++;
            console.log(`✅ Sample ${sample.sample_id} validated successfully`);
          } else {
            errorCount++;
            let errorMessage = 'Erreur inconnue';
            
            if (response.status === 401) {
              errorMessage = 'Session expirée - Reconnectez-vous';
            } else if (response.status === 404) {
              errorMessage = 'Endpoint de validation non trouvé';
            } else {
              try {
                const errorData = await response.json();
                errorMessage = errorData.message || `Erreur ${response.status}`;
              } catch {
                errorMessage = `Erreur HTTP ${response.status}`;
              }
            }
            
            errors.push(`${sample.sample_id}: ${errorMessage}`);
            console.warn(`❌ Validation failed for ${sample.sample_id}: ${errorMessage}`);
          }
        }
        
        console.log(`✅ Batch validation completed: ${successCount} successful, ${errorCount} errors`);
        
        // Show results
        if (successCount === batchSamples.length) {
          setSnackbarMessage(`✅ Lot ICP ${getBatchNumber(selectedSample)} ${validationDecision === 'APPROVE' ? 'approuvé' : 'rejeté'} avec succès - ${successCount} échantillons ${validationDecision === 'APPROVE' ? 'envoyés en génération de certificat' : 'renvoyés en analyse'}`);
        } else if (successCount > 0) {
          setSnackbarMessage(`⚠️ Validation partielle: ${successCount}/${batchSamples.length} échantillons validés. ${errorCount} erreurs.`);
        } else {
          setSnackbarMessage(`❌ Échec de la validation du lot: ${errors.join(', ')}`);
        }
        
      } else {
        // 🆕 SUPERVISOR EDITS: Prepare edited values for individual sample
        const sampleEdits = editedValues[selectedSample.id];
        const supervisorEdits = sampleEdits ? Object.entries(sampleEdits).map(([element, editedValue]) => {
          const originalValue = originalValues[selectedSample.id]?.[element] || 0;
          return {
            element,
            original_value: originalValue,
            edited_value: editedValue,
            edited_by: user?.id || '',
            edited_by_name: `${user?.firstName || ''} ${user?.lastName || ''}`,
            edited_at: new Date().toISOString(),
            edit_reason: validationNotes,
            change_magnitude: Math.abs(editedValue - originalValue),
            change_percentage: originalValue > 0 ? ((editedValue - originalValue) / originalValue) * 100 : 0
          };
        }) : [];

        // Individual sample validation
        const response = await fetch(`/api/v1/samples/${selectedSample.id}/validate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            decision: validationDecision,
            notes: validationNotes,
            validated_by: 'ICP Supervisor',
            laboratory: 'LABORATOIRE_ICP', // 🔥 CRITICAL FIX: Explicit laboratory
            // Add QA/QC data if present (BGM samples only)
            ...(showQaqcForm && qaqcStandards.length > 0 && {
              qaqc_data: {
                reference_standards: qaqcStandards,
                batch_info: {
                  analyst_name: selectedSample.metadonnees?.icp_analyst_name || 'N/A',
                  analysis_date: new Date().toISOString(),
                  batch_number: getBatchNumber(selectedSample) || 'N/A',
                  sample_count: 1
                }
              }
            }),
            // 🆕 SUPERVISOR EDITS: Include edited values with full audit trail
            ...(supervisorEdits.length > 0 && {
              supervisor_edits: {
                icp: supervisorEdits,
                validation_session_id: `validation_${Date.now()}`,
                total_edits: supervisorEdits.length
              }
            })
          })
        });

        if (response.ok) {
          const result = await response.json();
          setSnackbarMessage(`Échantillon ICP ${selectedSample.sample_id} ${validationDecision === 'APPROVE' ? 'approuvé et envoyé en génération de certificat' : 'rejeté et renvoyé en analyse'} avec succès`);
          // Debug log removed
        } else {
          let errorMessage = 'Erreur inconnue';
          
          if (response.status === 401) {
            errorMessage = 'Session expirée - Veuillez vous reconnecter';
          } else if (response.status === 404) {
            errorMessage = 'Endpoint de validation non trouvé';
          } else {
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || `Erreur ${response.status}`;
            } catch {
              errorMessage = `Erreur HTTP ${response.status}`;
            }
          }
          
          // Silenced in production
          setSnackbarMessage(`Erreur lors de la validation: ${errorMessage}`);
          setSnackbarOpen(true);
          return;
        }
      }
      
      setSnackbarOpen(true);
      setValidationDialog(false);
      setSelectedSample(null);
      setValidationNotes('');
      setValidationDecision('APPROVE');
      
      // 🆕 SUPERVISOR EDITS: Clear all edits after successful validation
      clearAllEdits();
      
      // Reset BIS selections
      setFirstBISSampleId(null);
      setSecondBISSampleId(null);
      
      // Reload the validation samples list to remove the validated samples
      await loadValidationSamples();
      
    } catch (error: any) {
      // Silenced in production
      const errorMessage = error?.message || String(error) || 'Erreur de connexion';
      
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        setSnackbarMessage('❌ Session expirée - Veuillez vous déconnecter et vous reconnecter');
      } else {
        setSnackbarMessage(`❌ Erreur lors de la validation: ${errorMessage}`);
      }
      
      setSnackbarOpen(true);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITIQUE': return 'error';
      case 'URGENTE': return 'warning';
      default: return 'primary';
    }
  };

  // Helper functions for batch processing
  const groupSamplesByBatch = (samples: SampleWithAnalyst[]) => {
    const batches: Record<string, SampleWithAnalyst[]> = {};
    const individualSamples: SampleWithAnalyst[] = [];

    samples.forEach(sample => {
      const batchInfo = sample.metadonnees?.batch_info || sample.metadonnees?.batch_analysis_summary;
      if (batchInfo && (batchInfo.is_batch_sample || batchInfo.batch_number)) {
        const batchNumber = batchInfo.batch_number;
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: SampleWithAnalyst): boolean => {
    return sample.metadonnees?.batch_info?.is_batch_sample === true || 
           sample.metadonnees?.batch_analysis_summary?.batch_number !== undefined;
  };

  const getBatchNumber = (sample: SampleWithAnalyst): string | null => {
    return sample.metadonnees?.batch_info?.batch_number || 
           sample.metadonnees?.batch_analysis_summary?.batch_number || null;
  };

  const getBatchPosition = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.sample_position || 
           sample.metadonnees?.batch_analysis_summary?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: SampleWithAnalyst): number => {
    return sample.metadonnees?.batch_info?.total_samples || 
           sample.metadonnees?.batch_analysis_summary?.total_samples || 1;
  };

  // 🔍 MULTI-LAB VALIDATION STATUS HELPERS - SAFE VISUAL INDICATORS ONLY
  const isMultiLabBatch = (batchSamples: SampleWithAnalyst[]): boolean => {
    return batchSamples.some(sample => 
      sample.metadonnees?.multi_lab_workflow === true || 
      sample.metadonnees?.bgm_method_selected === true
    );
  };
  
  // Keep old function for backward compatibility
  const isBGMBatch = (batchSamples: SampleWithAnalyst[]): boolean => {
    return batchSamples.some(sample => sample.metadonnees?.bgm_method_selected === true);
  };

  const getBGMValidationStatus = (batchSamples: SampleWithAnalyst[]) => {
    // 🔥 FIX: Use isMultiLabBatch to include custom multi-lab samples, not just BGM
    if (!isMultiLabBatch(batchSamples)) {
      return { isBGM: false, icpCompleted: false, auCompleted: false, awaitingAU: false };
    }

    // Check validation completion status across all samples in batch
    const icpCompleted = batchSamples.some(sample => 
      sample.metadonnees?.icp_validation_completed === true
    );
    
    const auCompleted = batchSamples.some(sample => 
      sample.metadonnees?.au_validation_completed === true ||
      sample.metadonnees?.au_validation?.validation_decision === 'APPROVED'
    );

    return {
      isBGM: true,
      icpCompleted,
      auCompleted,
      awaitingAU: icpCompleted && !auCompleted
    };
  };

  const handleValidateBatch = (batchSamples: SampleWithAnalyst[]) => {
    // For batch validation, we'll set the first sample as selectedSample
    // but we'll also store the entire batch for display
    if (batchSamples.length > 0) {
      setSelectedSample({
        ...batchSamples[0],
        // Add batch context to the sample
        batchSamples: batchSamples,
        isBatchValidation: true
      } as any);
      setValidationDialog(true);
      setValidationDecision('APPROVE');
      setValidationNotes('');
    }
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpandedBatches = new Set(expandedBatches);
    if (newExpandedBatches.has(batchNumber)) {
      newExpandedBatches.delete(batchNumber);
    } else {
      newExpandedBatches.add(batchNumber);
    }
    setExpandedBatches(newExpandedBatches);
  };

  const getQCStatusColor = (status: string) => {
    switch (status) {
      case 'PASSED':
        return 'success';
      case 'WARNING':
        return 'warning';
      case 'FAILED':
        return 'error';
      default:
        return 'default';
    }
  };

  const loadSupervisorData = async () => {
    await loadDashboardData();
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          borderRadius: 3,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Chargement du tableau de bord superviseur ICP...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', bgcolor: 'background.default', minHeight: '100vh' }}>
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={(_, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Dashboard />} label="Vue d'Ensemble ICP" />
          <Tab icon={<People />} label="Performance Équipe" />
          <Tab icon={<Assessment />} label="Validation Échantillons" />
        </Tabs>
      </Paper>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 3 }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Vue d'Ensemble ICP Tab */}
      {currentTab === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
            🎛️ Supervision Laboratoire ICP
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Bonjour, {user?.firstName} - Superviseur ICP • Configuration méthodes • Validation lots • Contrôle qualité
          </Typography>

          {/* Enhanced Stats Dashboard with Premium Styling */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              {
                icon: Assignment,
                value: stats.pending_validation,
                label: 'En attente validation',
                color: theme.palette.warning.main,
                emoji: '⏳',
                gradient: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)'
              },
              {
                icon: CheckCircle,
                value: stats.validated_today,
                label: 'Validés aujourd\'hui',
                color: theme.palette.success.main,
                emoji: '✅',
                gradient: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
              },
              {
                icon: Error,
                value: stats.rejected_today,
                label: 'Rejetés aujourd\'hui',
                color: theme.palette.error.main,
                emoji: '❌',
                gradient: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)'
              },
              {
                icon: Timer,
                value: stats.overdue_validation,
                label: 'Validations en retard',
                color: theme.palette.error.main,
                emoji: '🚨',
                gradient: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)'
              },
              {
                icon: Science,
                value: stats.total_in_lab,
                label: 'Total en laboratoire',
                color: theme.palette.info.main,
                emoji: '🔬',
                gradient: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)'
              },
              {
                icon: Speed,
                value: `${stats.quality_pass_rate.toFixed(1)}%`,
                label: 'Taux de réussite QC',
                color: theme.palette.success.main,
                emoji: '🎯',
                gradient: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)'
              },
            ].map((stat, index) => (
              <Grid item xs={12} sm={6} md={2} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 4,
                    background: `linear-gradient(135deg, ${alpha(stat.color, 0.1)} 0%, ${alpha(stat.color, 0.05)} 100%)`,
                    borderLeft: `6px solid ${stat.color}`,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': { 
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      transform: 'translateY(-4px)',
                      background: `linear-gradient(135deg, ${alpha(stat.color, 0.15)} 0%, ${alpha(stat.color, 0.08)} 100%)`,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '50px',
                      height: '50px',
                      background: `linear-gradient(135deg, ${alpha(stat.color, 0.2)}, transparent)`,
                      borderRadius: '0 0 0 50px',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center', position: 'relative' }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mb: 2,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          background: stat.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 4px 16px ${alpha(stat.color, 0.3)}`,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 2,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                          }
                        }}
                      >
                        <Typography 
                          sx={{ 
                            fontSize: '1.5rem',
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          {stat.emoji}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 800,
                        background: stat.gradient,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                        mb: 1,
                        lineHeight: 1.2
                      }}
                    >
                      {stat.value}
                    </Typography>
                    
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Enhanced Instrument Status */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card 
                sx={{ 
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                  borderLeft: `6px solid ${theme.palette.success.main}`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': { 
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.light, 0.08)} 100%)`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '60px',
                    height: '60px',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.2)}, transparent)`,
                    borderRadius: '0 0 0 60px',
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.5rem' }}>📊</Typography>
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Statut Instrument ICP
                    </Typography>
                  </Box>
                  
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mb: 2,
                      borderRadius: 3,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                      '& .MuiAlert-icon': {
                        fontSize: '1.5rem'
                      }
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      🟢 Instrument ICP opérationnel - Uptime: {stats.instrument_uptime}%
                    </Typography>
                  </Alert>
                  
                  <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: alpha(theme.palette.grey[100], 0.5) }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                      ⚡ Plasma stable • RF Power: 1.2 kW • Température: 23°C • Humidité: 45%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      🔧 Dernière maintenance: Il y a 3 jours • Prochaine calibration: Dans 2 jours
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                sx={{ 
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
                  borderLeft: `6px solid ${theme.palette.info.main}`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': { 
                    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.15)} 0%, ${alpha(theme.palette.info.light, 0.08)} 100%)`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '50px',
                    height: '50px',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.2)}, transparent)`,
                    borderRadius: '0 0 0 50px',
                  }
                }}
              >
                <CardContent sx={{ p: 3, position: 'relative' }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                      }}
                    >
                      <Typography sx={{ fontSize: '1.2rem' }}>⚡</Typography>
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(45deg, #2196f3, #42a5f5)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Performance Équipe
                    </Typography>
                  </Box>
                  
                  {Object.entries(performanceMetrics.analyst_efficiency).length > 0 ? (
                    Object.entries(performanceMetrics.analyst_efficiency).map(([analyst, count]) => (
                      <Box 
                        key={analyst} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          mb: 1,
                          p: 1.5,
                          borderRadius: 2,
                          background: alpha(theme.palette.info.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            background: alpha(theme.palette.info.main, 0.1),
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>👨‍🔬 {analyst}</Typography>
                        <Typography variant="body2" fontWeight={700} color="info.main">
                          {count} échantillons
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Box 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        borderRadius: 2,
                        background: alpha(theme.palette.grey[400], 0.1),
                        border: `1px dashed ${alpha(theme.palette.grey[400], 0.3)}`
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        📊 Aucune donnée de performance disponible
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: alpha(theme.palette.grey[100], 0.5) }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      ⏱️ Délai moyen: <span style={{ color: theme.palette.info.main, fontWeight: 700 }}>
                        {performanceMetrics.average_turnaround_time}h
                      </span>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Performance Équipe Tab */}
      {currentTab === 1 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <People sx={{ mr: 1, verticalAlign: 'middle' }} />
            Performance de l'Équipe ICP
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    📈 Productivité Analystes
                  </Typography>
                  
                  {Object.entries(performanceMetrics.analyst_efficiency).length > 0 ? (
                    Object.entries(performanceMetrics.analyst_efficiency).map(([analyst, efficiency]) => (
                      <Box key={analyst} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body1">{analyst}</Typography>
                          <Typography variant="body1" fontWeight={600}>{efficiency} éch/jour</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(efficiency / 20) * 100} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune donnée de performance disponible
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 4 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    🎯 Métriques Qualité
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Taux de réussite QC</Typography>
                    <Typography variant="h5" color="success.main" fontWeight={700}>
                      {performanceMetrics.quality_pass_rate.toFixed(1)}%
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Uptime Instrument</Typography>
                    <Typography variant="h5" color="info.main" fontWeight={700}>
                      {performanceMetrics.instrument_uptime}%
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">Échantillons traités</Typography>
                    <Typography variant="h5" color="primary.main" fontWeight={700}>
                      {performanceMetrics.total_samples_processed}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Validation Échantillons Tab */}
      {currentTab === 2 && (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">
              <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Validation des Échantillons ICP
              {totalSamples > 0 && (
                <Chip 
                  label={`${totalSamples} total • ${batchCount} lots`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            
            {/* 🔍 SEARCH BAR */}
            <TextField
              size="small"
              placeholder="Rechercher par ID ou N° Lot..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                width: { xs: '100%', sm: 300 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {Array.isArray(samples) && samples.length > 0 ? (
            <Box>
              {(() => {
                const { batches, individualSamples } = groupSamplesByBatch(samples);
                const hasBatches = Object.keys(batches).length > 0;
                const hasIndividual = individualSamples.length > 0;

                return (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>N° Échantillon / Lot</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Client</TableCell>
                          <TableCell>Priorité</TableCell>
                          <TableCell>Analyste</TableCell>
                          <TableCell>Date Analyse</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Batch rows */}
                        {Object.entries(batches).map(([batchNumber, batchSamples]) => {
                          const isExpanded = expandedBatches.has(batchNumber);
                          
                          return (
                            <React.Fragment key={batchNumber}>
                              {/* Batch Header Row - Enhanced Professional Styling */}
                              <TableRow 
                                sx={{ 
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
                                  cursor: 'pointer',
                                  borderLeft: `6px solid ${theme.palette.primary.main}`,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': { 
                                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.secondary.main, 0.25)} 100%)`,
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                                onClick={() => toggleBatchExpansion(batchNumber)}
                              >
                                <TableCell colSpan={8} sx={{ py: 2 }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={2}>
                                      <IconButton 
                                        size="small" 
                                        sx={{ 
                                          p: 1,
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
                                          borderRadius: 2
                                        }}
                                      >
                                        {isExpanded ? <ExpandLess color="primary" /> : <ExpandMoreIcon color="primary" />}
                                      </IconButton>
                                      
                                      <Chip 
                                        label="🔬 LOT ICP VALIDATION" 
                                        sx={{ 
                                          fontWeight: 'bold',
                                          fontSize: '0.9rem',
                                          px: 2,
                                          py: 1,
                                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                          color: 'white',
                                          boxShadow: '0 3px 12px rgba(25, 118, 210, 0.4)',
                                          '&:hover': {
                                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                                          }
                                        }}
                                      />
                                      
                                      {/* 🔍 BGM STATUS INDICATOR - VISUAL ONLY */}
                                      {(() => {
                                        const bgmStatus = getBGMValidationStatus(batchSamples);
                                        if (bgmStatus.isBGM && bgmStatus.awaitingAU) {
                                          return (
                                            <Chip 
                                              label="⏳ EN ATTENTE DE VALIDATION AU" 
                                              sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                                background: `linear-gradient(45deg, #ff9800, #ffc107)`,
                                                color: 'white',
                                                boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)',
                                                animation: 'pulse 2s infinite',
                                                '@keyframes pulse': {
                                                  '0%': { opacity: 1 },
                                                  '50%': { opacity: 0.7 },
                                                  '100%': { opacity: 1 }
                                                }
                                              }}
                                            />
                                          );
                                        } else if (bgmStatus.isBGM && bgmStatus.auCompleted) {
                                          return (
                                            <Chip 
                                              label="✅ BGM COMPLET (ICP + AU)" 
                                              sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                                background: `linear-gradient(45deg, #4caf50, #8bc34a)`,
                                                color: 'white',
                                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.4)',
                                              }}
                                            />
                                          );
                                        } else if (bgmStatus.isBGM) {
                                          return (
                                            <Chip 
                                              label="🔬 ÉCHANTILLON BGM" 
                                              sx={{ 
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                px: 1.5,
                                                py: 0.5,
                                                background: `linear-gradient(45deg, #9c27b0, #e91e63)`,
                                                color: 'white',
                                                boxShadow: '0 2px 8px rgba(156, 39, 176, 0.4)',
                                              }}
                                            />
                                          );
                                        }
                                        return null;
                                      })()}
                                      
                                      <Typography 
                                        variant="h5" 
                                        sx={{ 
                                          fontWeight: 800,
                                          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                          backgroundClip: 'text',
                                          WebkitBackgroundClip: 'text',
                                          WebkitTextFillColor: 'transparent',
                                          textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                      >
                                        {batchNumber}
                                      </Typography>
                                      
                                      <Chip 
                                        label={`📊 ${batchSamples.length} échantillons`}
                                        sx={{ 
                                          backgroundColor: alpha(theme.palette.info.main, 0.15),
                                          color: theme.palette.info.main,
                                          fontWeight: 600,
                                          border: `2px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                          fontSize: '0.85rem'
                                        }}
                                      />
                                      
                                      <Chip 
                                        label={`⚡ ${batchSamples[0]?.metadonnees?.icp_analyst_name || 'ICP Analyst'}`}
                                        sx={{ 
                                          backgroundColor: alpha(theme.palette.success.main, 0.15),
                                          color: theme.palette.success.main,
                                          fontWeight: 600,
                                          border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                          fontSize: '0.85rem'
                                        }}
                                      />
                                    </Box>
                                    
                                    <Button
                                      variant="contained"
                                      size="large"
                                      startIcon={<CheckCircle />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleValidateBatch(batchSamples);
                                      }}
                                      sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                        px: 3,
                                        py: 1.5,
                                        borderRadius: 3,
                                        background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)',
                                        textTransform: 'none',
                                        '&:hover': { 
                                          background: `linear-gradient(45deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                                          boxShadow: '0 6px 24px rgba(76, 175, 80, 0.6)',
                                          transform: 'translateY(-2px)'
                                        },
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      🚀 Valider le Lot
                                    </Button>
                                  </Box>
                                </TableCell>
                              </TableRow>

                              {/* Batch Sample Rows - Enhanced Styling */}
                              {isExpanded && batchSamples.map((sample, index) => (
                                <TableRow 
                                  key={sample.id} 
                                  sx={{ 
                                    background: index % 2 === 0 
                                      ? `linear-gradient(90deg, ${alpha(theme.palette.grey[50], 0.8)} 0%, ${alpha(theme.palette.grey[100], 0.6)} 100%)`
                                      : `linear-gradient(90deg, ${alpha(theme.palette.info.light, 0.4)} 0%, ${alpha(theme.palette.info.light, 0.2)} 100%)`,
                                    borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                    }
                                  }}
                                >
                                  <TableCell sx={{ pl: 6 }}>
                                    <Box display="flex" alignItems="center" gap={1.5}>
                                      <Box 
                                        sx={{ 
                                          width: 8, 
                                          height: 8, 
                                          borderRadius: '50%', 
                                          backgroundColor: theme.palette.primary.main,
                                          boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`
                                        }} 
                                      />
                                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Position {getBatchPosition(sample)}:
                                      </Typography>
                                      <Typography 
                                        sx={{ 
                                          fontWeight: 700, 
                                          color: theme.palette.primary.main,
                                          fontSize: '0.95rem'
                                        }}
                                      >
                                        {sample.sample_id}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.type_echantillon} 
                                      size="small" 
                                      sx={{ 
                                        backgroundColor: alpha(theme.palette.grey[600], 0.1),
                                        color: theme.palette.grey[700],
                                        fontWeight: 500
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                      {sample.client?.company_name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.priorite} 
                                      size="small" 
                                      color={getPriorityColor(sample.priorite)}
                                      sx={{ 
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        boxShadow: 1
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Box 
                                        sx={{ 
                                          width: 6, 
                                          height: 6, 
                                          borderRadius: '50%', 
                                          backgroundColor: theme.palette.success.main 
                                        }} 
                                      />
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {sample.analyst_name || sample.metadonnees?.icp_analyst_name || 'ICP Analyst'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(sample.date_modification).toLocaleDateString('fr-FR')}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={sample.statut} 
                                      size="small" 
                                      sx={{ 
                                        backgroundColor: alpha(theme.palette.info.main, 0.15),
                                        color: theme.palette.info.main,
                                        fontWeight: 600,
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      startIcon={<Visibility />}
                                      onClick={() => handleValidateSample(sample)}
                                      sx={{ 
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderColor: theme.palette.primary.main,
                                        '&:hover': {
                                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                          borderColor: theme.palette.primary.dark,
                                          boxShadow: 2
                                        }
                                      }}
                                    >
                                      Voir
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </React.Fragment>
                          );
                        })}

                        {/* Individual samples - Enhanced Styling */}
                        {individualSamples.map((sample, index) => (
                          <TableRow 
                            key={sample.id}
                            sx={{
                              background: `linear-gradient(90deg, ${alpha(theme.palette.grey[50], 0.9)} 0%, ${alpha(theme.palette.grey[100], 0.7)} 100%)`,
                              borderLeft: `4px solid ${alpha(theme.palette.warning.main, 0.6)}`,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                background: `linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.light, 0.12)} 100%)`,
                                borderLeft: `4px solid ${theme.palette.warning.main}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                              }
                            }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1.5}>
                                <Box 
                                  sx={{ 
                                    width: 8, 
                                    height: 8, 
                                    borderRadius: '50%', 
                                    backgroundColor: theme.palette.warning.main,
                                    boxShadow: `0 0 8px ${alpha(theme.palette.warning.main, 0.5)}`
                                  }} 
                                />
                                <Typography 
                                  sx={{ 
                                    fontWeight: 700, 
                                    color: theme.palette.warning.dark,
                                    fontSize: '0.95rem'
                                  }}
                                >
                                  {sample.sample_id}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.type_echantillon} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: alpha(theme.palette.grey[600], 0.1),
                                  color: theme.palette.grey[700],
                                  fontWeight: 500
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                {sample.client?.company_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.priorite} 
                                size="small" 
                                color={getPriorityColor(sample.priorite)}
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  boxShadow: 1
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box 
                                  sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    backgroundColor: theme.palette.success.main 
                                  }} 
                                />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {sample.analyst_name || sample.metadonnees?.icp_analyst_name || 'ICP Analyst'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(sample.date_modification).toLocaleDateString('fr-FR')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.statut} 
                                size="small" 
                                sx={{ 
                                  backgroundColor: alpha(theme.palette.info.main, 0.15),
                                  color: theme.palette.info.main,
                                  fontWeight: 600,
                                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<CheckCircle />}
                                onClick={() => handleValidateSample(sample)}
                                sx={{ 
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                                  boxShadow: 2,
                                  '&:hover': {
                                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                    boxShadow: 4,
                                    transform: 'translateY(-1px)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                Valider
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
              
              {/* 📄 PAGINATION CONTROLS */}
              <TablePagination
                component="div"
                count={totalSamples}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[50, 100, 250, 500]}
                labelRowsPerPage="Échantillons par page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} sur ${count} échantillons (${batchCount} lots sur cette page)`
                }
                sx={{
                  borderTop: '1px solid #e0e0e0',
                  mt: 2,
                  '& .MuiTablePagination-toolbar': {
                    backgroundColor: '#f5f5f5',
                  }
                }}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Aucun échantillon en attente de validation ICP
            </Alert>
          )}
        </Box>
      )}

      {/* Validation Dialog - Enhanced like SAA */}
      <Dialog 
        open={validationDialog} 
        onClose={() => {
          setValidationDialog(false);
          setFirstBISSampleId(null);
          setSecondBISSampleId(null);
          clearAllEdits(); // 🆕 Clear edits on dialog close
        }} 
        maxWidth="lg" 
        fullWidth
        fullScreen
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <VerifiedUser color="primary" sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🔬 Validation ICP - {(selectedSample as any)?.isBatchValidation ? 'Lot' : 'Échantillon'} 
                {(selectedSample as any)?.isBatchValidation ? (selectedSample ? getBatchNumber(selectedSample) : '') : selectedSample?.sample_id}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {(selectedSample as any)?.isBatchValidation 
                  ? `${(selectedSample as any)?.batchSamples?.length || 0} échantillons dans le lot`
                  : 'Validation individuelle'
                } • Laboratoire ICP • Superviseur: {user?.firstName} {user?.lastName}
              </Typography>
              
              {/* 🔍 BGM STATUS INFO IN DIALOG - VISUAL ONLY */}
              {(selectedSample as any)?.isBatchValidation && (() => {
                const bgmStatus = getBGMValidationStatus((selectedSample as any)?.batchSamples || []);
                if (bgmStatus.isBGM) {
                  return (
                    <Typography variant="body2" sx={{ 
                      mt: 1, 
                      p: 1, 
                      borderRadius: 1,
                      backgroundColor: bgmStatus.awaitingAU ? alpha('#ff9800', 0.1) : alpha('#4caf50', 0.1),
                      color: bgmStatus.awaitingAU ? '#e65100' : '#2e7d32',
                      fontWeight: 'bold'
                    }}>
                      🔬 <strong>Échantillon Multi-Lab:</strong> {
                        bgmStatus.awaitingAU 
                          ? '⏳ Validation ICP terminée - En attente de validation AU pour génération de certificat'
                          : bgmStatus.auCompleted 
                            ? '✅ Validations ICP et AU terminées - Prêt pour certificat'
                            : '🔄 En cours de validation ICP'
                      }
                    </Typography>
                  );
                }
                return null;
              })()}
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          {selectedSample && (
            <Box sx={{ mt: 1 }}>
              {(selectedSample as any)?.isBatchValidation ? (
                /* Batch Validation View - Enhanced with Complete Information */
                <Box>
                  {/* Comprehensive Batch Information Section */}
                  <Card sx={{ mb: 2, backgroundColor: '#e8f5e8', border: '2px solid #4caf50' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2e7d32', mb: 2 }}>
                        📊 Récapitulatif Complet du Lot ICP {selectedSample ? getBatchNumber(selectedSample) : 'N/A'}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {/* Batch Summary */}
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Échantillons</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1.2rem' }}>
                              {(selectedSample as any)?.batchSamples?.length || 0}
                            </Typography>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Analyste ICP</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {(selectedSample as any)?.batchSamples?.[0]?.metadonnees?.icp_analyst_name || 'N/A'}
                            </Typography>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Éléments Analysés</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {(() => {
                                const sample = (selectedSample as any)?.batchSamples?.[0];
                                // 🔧 FIX: Use getSampleElements with includeAuForBGM for validation modal
                                const elements = sample ? getSampleElements(sample, true) : [];
                                return elements.length || 0;
                              })()}
                            </Typography>
                          </Card>
                        </Grid>
                        
                        <Grid item xs={12} sm={6} md={3}>
                          <Card variant="outlined" sx={{ p: 1, backgroundColor: 'white' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Date Analyse</Typography>
                            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {(() => {
                                const analysisDate = (selectedSample as any)?.batchSamples?.[0]?.metadonnees?.icp_analysis_completed_at ||
                                                    (selectedSample as any)?.batchSamples?.[0]?.date_fin_analyse;
                                return analysisDate ? new Date(analysisDate).toLocaleDateString('fr-FR') : 'N/A';
                              })()}
                            </Typography>
                          </Card>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                  
                  {/* COMPREHENSIVE ANALYSIS + PREPARATION TABLE */}
                  <Card sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
                        📊 Tableau Complet: Préparation Chimique + Résultats d'Analyse ICP
                      </Typography>
                      
                      {/* Elements analyzed overview */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 1 }}>
                          Éléments analysés:
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {(() => {
                            const sample = (selectedSample as any)?.batchSamples?.[0];
                            const isBGM = sample?.metadonnees?.bgm_method_selected === true || sample?.metadonnees?.multi_lab_workflow === true;
                            // 🔧 FIX: Use getSampleElements with includeAuForBGM for validation modal
                            const elements = sample ? getSampleElements(sample, true) : [];
                            return elements.map((element: string) => (
                              <Chip 
                                key={element} 
                                label={element} 
                                size="small"
                                color={element === 'Au' && isBGM ? 'warning' : 'primary'} 
                                variant="outlined"
                                title={element === 'Au' && isBGM ? '⚖️ Valeur du laboratoire AU (pour comparaison)' : undefined}
                                sx={{ fontSize: '0.7rem', height: '24px' }}
                              />
                            ));
                          })()}
                        </Box>
                      </Box>
                      
                      {/* COMPREHENSIVE MATRIX TABLE - PREPARATION + ANALYSIS */}
                      <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {/* Sample and Preparation Data Columns */}
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 120 }}>Échantillon</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: 200 }}>Client / Référence</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 100 }}>Poids Initial (g)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 120 }}>Méthode Digestion</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 100 }}>Volume Final (mL)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 100 }}>Facteur Dilution</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e8f5e8', minWidth: 120 }}>Technicien</TableCell>
                              
                              {/* ALL ELEMENTS COLUMNS WITH METHOD-UNIT HEADER */}
                              {/* 🔧 FIX: Use ALL elements from batch (union) with AU for BGM samples */}
                              {(() => {
                                const batchSamples = (selectedSample as any)?.batchSamples || [];
                                // 🆕 Include AU for BGM samples in validation modal
                                const elements = getAllBatchElements(batchSamples, true);
                                const sample = batchSamples[0];
                                const isBGM = sample?.metadonnees?.bgm_method_selected === true || sample?.metadonnees?.multi_lab_workflow === true;
                                const icpMethodCode = sample?.metadonnees?.analyses_demandees?.find((m: string) =>
                                  m?.includes('ICP') || m?.includes('D4A') || m?.includes('FP') || m?.includes('BGM') || m?.includes('ANA/082') || m?.includes('ANA/083') || m?.includes('ANA/038')
                                ) || 'D4A-ICP- PRE.MO/ANA/038';

                                return elements.map((element: string) => {
                                  const limit = getMethodDetectionLimit(icpMethodCode, element);
                                  const unitLabel = limit?.unit || 'ppm';
                                  const isAuFromAULab = element === 'Au' && isBGM;
                                  return (
                                    <TableCell key={`${element}-ppm`} sx={{ 
                                      minWidth: 80, 
                                      fontWeight: 'bold', 
                                      // 🔧 BGM FIX: Highlight AU column differently (comes from AU lab)
                                      backgroundColor: isAuFromAULab ? '#ff9800' : '#ffeb3b', 
                                      textAlign: 'center',
                                      borderLeft: isAuFromAULab ? '3px solid #e65100' : '2px solid #fbc02d'
                                    }}>
                                      {element} ({unitLabel})
                                      {isAuFromAULab && <Typography component="span" sx={{ fontSize: '0.6rem', display: 'block', color: '#fff' }}>⚖️ AU Lab</Typography>}
                                    </TableCell>
                                  );
                                });
                              })()}
                              
                              {/* Removed percentage headers for ICP supervisor */}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* 🔧 FIX: Sort samples by sample_id in ascending order (first sample at top) */}
                            {(selectedSample as any)?.batchSamples
                              ?.slice() // Create a copy to avoid mutating original
                              ?.sort((a: any, b: any) => {
                                // Extract numeric suffix from sample_id (e.g., "AFL-0072-01" -> 1)
                                const getNum = (id: string) => {
                                  const parts = id?.split('-') || [];
                                  return parseInt(parts[parts.length - 1]) || 0;
                                };
                                return getNum(a.sample_id) - getNum(b.sample_id);
                              })
                              ?.map((sample: any, sampleIndex: number) => {
                              const prepData = sample.metadonnees?.icp_preparation || sample.metadonnees?.icp_preparation_data || {};
                              
                              // 🔧 CRITICAL FIX: For ICP Supervisor, prioritize ICP-specific data sources for ICP elements
                              // But for Au, always get from analysis_results (comes from AU lab)
                              // Priority: icp_analysis_form -> icp_analysis_data -> analysis_results (fallback)
                              const icpAnalysisSources = [
                                sample.metadonnees?.icp_analysis_form?.analysis_readings,
                                sample.metadonnees?.icp_analysis_data?.analysis_readings,
                                sample.metadonnees?.analysis_results
                              ];
                              
                              let icpReadings = [];
                              for (const source of icpAnalysisSources) {
                                if (source && Array.isArray(source) && source.length > 0) {
                                  icpReadings = source;
                                  break;
                                }
                              }
                              
                              // For BGM samples, get Au from au_analysis_form or analysis_results
                              const analysisResults = sample.metadonnees?.analysis_results || [];
                              const auAnalysisReadings = sample.metadonnees?.au_analysis_form?.analysis_readings || [];
                              
                              // Try to get Au from analysis_results first
                              let auData = analysisResults.find((r: any) => r.element === 'Au');
                              
                              // If Au is 0 or missing in analysis_results, get from au_analysis_form
                              if ((!auData || auData.concentration_ppm === 0) && auAnalysisReadings.length > 0) {
                                const auFormData = auAnalysisReadings.find((r: any) => r.element === 'Au');
                                if (auFormData) {
                                  // Convert au_analysis_form format to standard format
                                  auData = {
                                    element: 'Au',
                                    concentration_ppm: auFormData.ppm || auFormData.grams_per_tonne || 0,
                                    detection_status: 'VALID',
                                    timestamp: auFormData.timestamp
                                  };
                                }
                              }
                              
                              // Merge: ICP elements from icpReadings + Au from AU lab
                              let analysisReadings = [...icpReadings];
                              if (auData && !icpReadings.some((r: any) => r.element === 'Au')) {
                                analysisReadings.push(auData);
                              } else if (auData) {
                                // Replace Au in icpReadings with Au from AU lab (more accurate)
                                analysisReadings = icpReadings.map((r: any) => 
                                  r.element === 'Au' ? auData : r
                                );
                              }
                              
                              // Create a map for quick element lookup
                              const elementData: Record<string, any> = {};
                              analysisReadings.forEach((reading: any) => {
                                elementData[reading.element] = reading;
                              });
                              
                              // 🔧 FIX: Use ALL batch elements for column consistency, with AU for BGM
                              const batchSamples = (selectedSample as any)?.batchSamples || [];
                              // 🆕 Include AU for BGM samples in validation modal
                              const allBatchElements = getAllBatchElements(batchSamples, true);
                              const sampleSelectedElements = getSampleElements(sample, true);
                              
                              return (
                                <TableRow key={sampleIndex} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                                  {/* Sample ID */}
                                  <TableCell sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.8rem' }}>
                                    {sample.sample_id}
                                  </TableCell>
                                  
                                  {/* Client / Reference */}
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {sample.client?.company_name || 'N/A'}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                        Réf: {sample.external_id || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  
                                  {/* Full Preparation Data */}
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Chip 
                                      label={prepData.initialWeight?.toFixed(3) || prepData.initial_weight?.toFixed(3) || 'N/A'} 
                                      size="small"
                                      color={prepData.initialWeight || prepData.initial_weight ? 'success' : 'error'}
                                      sx={{ fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Chip 
                                      label={prepData.digestionMethod || prepData.digestion_method || 'N/A'} 
                                      size="small"
                                      color={prepData.digestionMethod || prepData.digestion_method ? 'primary' : 'default'}
                                      sx={{ fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {prepData.digestionVolume || prepData.initial_volume || prepData.finalVolume || 'N/A'} mL
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Chip 
                                      label={`${prepData.dilutionFactor || prepData.total_dilution_factor || 'N/A'}x`} 
                                      size="small"
                                      color="secondary"
                                      sx={{ fontSize: '0.65rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem' }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                      {prepData.technician || prepData.technician_name || 'N/A'}
                                    </Typography>
                                  </TableCell>
                                  
                                  {/* ALL PPM VALUES FOR ALL ELEMENTS WITH GOLDEN RULE */}
                                  {/* 🔧 FIX: Iterate over ALL batch elements, grey out non-selected per sample */}
                                  {allBatchElements.map((element: string) => {
                                    const hasElement = sampleSelectedElements.includes(element);
                                    const reading = elementData[element];
                                    const ppm = reading?.concentration_ppm || 0;
                                    
                                    // 🆕 BGM FIX: Check if this is AU element from AU lab (for visual distinction)
                                    const isBGMSample = sample.metadonnees?.bgm_method_selected === true || sample.metadonnees?.multi_lab_workflow === true;
                                    const isAuFromAULab = element === 'Au' && isBGMSample;
                                    
                                    // 🔧 FIX: If element not selected for this sample, show greyed disabled cell
                                    if (!hasElement) {
                                      return (
                                        <TableCell key={`ppm-${sample.id}-${element}`} sx={{ 
                                          borderLeft: '1px solid #ffeb3b', 
                                          p: 0.5, 
                                          textAlign: 'center',
                                          backgroundColor: '#f5f5f5'
                                        }}>
                                          <Typography sx={{ color: '#bdbdbd', fontSize: '0.75rem' }}>-</Typography>
                                        </TableCell>
                                      );
                                    }
                                    
                                    // Get method code for method-specific detection limits
                                    const icpMethodCode = sample.metadonnees?.analyses_demandees?.find((method: string) => 
                                      method.includes('ICP') || method.includes('D4A') || method.includes('FP') || method.includes('BGM') || method.includes('ANA/082') || method.includes('ANA/083') || method.includes('ANA/038')
                                    ) || 'D4A-ICP- PRE.MO/ANA/038';

                                    // Use the same method-driven formatter as certificates (frontend replica)
                                    const formatted = formatWithMethodDetectionLimitFrontend(icpMethodCode, element, ppm);
                                    
                                    // Check detection limits
                                    const validation = methodValidations.find(v => v.element === element);
                                    // Keep LOQ highlighting from local validations; LOD is shown in string
                                    const belowLOD = formatted?.startsWith('<');
                                    const belowLOQ = validation && ppm >= (validation.lod_ppm || 0) && ppm < (validation.loq_ppm || 0);
                                    
                                    // Apply golden rule for display
                                    let displayValue = ppm;
                                    let displayUnit: 'ppm' | '%' = 'ppm';
                                    if (ppm > 10000) {
                                      displayValue = ppm / 10000;
                                      displayUnit = '%';
                                    }
                                    
                                    // 🆕 SUPERVISOR EDIT: Get edited value if exists
                                    const currentValue = editedValues[sample.id]?.[element] ?? ppm;
                                    const isEdited = editedValues[sample.id]?.[element] !== undefined;

                                    return (
                                      <TableCell key={`ppm-${sample.id}-${element}`} sx={{ 
                                        // 🆕 BGM FIX: Highlight AU values differently (from AU lab)
                                        borderLeft: isAuFromAULab ? '3px solid #e65100' : '1px solid #ffeb3b', 
                                        p: 0.5, 
                                        textAlign: 'center',
                                        backgroundColor: isAuFromAULab 
                                          ? (isEdited ? '#ffe0b2' : '#fff3e0')
                                          : (isEdited ? '#fffacd' : (belowLOD ? '#ffebee' : belowLOQ ? '#fff3e0' : 'transparent'))
                                      }}>
                                        {/* 🆕 EDITABLE TextField for supervisor to modify values */}
                                        <TextField
                                          type="number"
                                          value={currentValue}
                                          onChange={(e) => {
                                            const newVal = parseFloat(e.target.value);
                                            if (!isNaN(newVal) && newVal >= 0) {
                                              handleValueEdit(sample.id, element, newVal, ppm);
                                            }
                                          }}
                                          inputProps={{ 
                                            step: element === 'Au' ? 0.01 : 0.1,
                                            min: 0,
                                            style: { textAlign: 'center', padding: '4px' }
                                          }}
                                          size="small"
                                          sx={{ 
                                            width: '90px',
                                            '& input': {
                                              fontSize: '0.75rem',
                                              fontWeight: isEdited ? 'bold' : 'normal',
                                              color: isEdited ? '#d32f2f' : (ppm > 0 ? '#1976d2' : '#999'),
                                            },
                                            '& .MuiOutlinedInput-root': {
                                              '& fieldset': {
                                                borderColor: isEdited ? '#ff9800' : 'rgba(0, 0, 0, 0.23)',
                                                borderWidth: isEdited ? 2 : 1,
                                              },
                                            }
                                          }}
                                        />
                                        {/* Show edit indicator */}
                                        {isEdited && (
                                          <Tooltip title={`Original: ${ppm.toFixed(2)} → Edited: ${currentValue.toFixed(2)}`}>
                                            <Chip 
                                              label="✏️" 
                                              size="small" 
                                              color="warning" 
                                              sx={{ 
                                                ml: 0.5, 
                                                height: 16, 
                                                fontSize: '0.65rem',
                                                '& .MuiChip-label': { px: 0.5 }
                                              }} 
                                            />
                                          </Tooltip>
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                  
                                  {/* Removed duplicate percentage columns for ICP supervisor - ppm only */}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Batch Summary - Same as ICP Analyst Dashboard */}
                      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Échantillons</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                              {(selectedSample as any)?.batchSamples?.length || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Éléments</Typography>
                            <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                              {(() => {
                                const sample = (selectedSample as any)?.batchSamples?.[0];
                                // 🔧 FIX: Include AU for BGM in validation modal
                                return getSampleElements(sample, true) || [];
                              })().length || 0}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Mesures Complétées</Typography>
                            <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem' }}>
                              {(() => {
                                let completedCount = 0;
                                let totalRequired = 0;
                                
                                (selectedSample as any)?.batchSamples?.forEach((sample: any) => {
                                  const analysisReadings = sample.metadonnees?.icp_analysis_data?.analysis_readings || 
                                                         sample.metadonnees?.icp_analysis_form?.analysis_readings || 
                                                         sample.metadonnees?.analysis_results || [];
                                  
                                  // 🔧 FIX: Include AU for BGM in validation modal
                                  const elementsToAnalyze = getSampleElements(sample, true);
                                  totalRequired += elementsToAnalyze.length;
                                  
                                  analysisReadings.forEach((reading: any) => {
                                    if (reading.concentration_ppm > 0) {
                                      completedCount++;
                                    }
                                  });
                                });
                                
                                return `${completedCount}/${totalRequired}`;
                              })()}
                            </Typography>
                          </Grid>
                          <Grid item xs={3}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>Statut QC</Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', color: '#1976d2' }}>
                              LOD/LOQ Vérifié
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                /* Individual Sample Validation View */
                <Box>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Informations Échantillon ICP</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Client:</Typography>
                          <Typography variant="body1">{selectedSample.client?.company_name || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">Type d'échantillon:</Typography>
                          <Typography variant="body1">{selectedSample.type_echantillon}</Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* 🧪 QA/QC DATA COLLECTION - BGM SAMPLES ONLY */}
              {(selectedSample as any)?.isBatchValidation && 
               isBGMBatch((selectedSample as any)?.batchSamples || []) && 
               validationDecision === 'APPROVE' && (
                <Card sx={{ mt: 2, backgroundColor: alpha('#2196f3', 0.05), border: `2px solid ${theme.palette.info.main}` }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1976d2' }}>
                        🧪 Données QA/QC (Échantillons BGM uniquement)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={loadDefaultQAQCTemplate}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        📋 Charger Template Par Défaut
                      </Button>
                    </Box>
                    
                    {!showQaqcForm && (
                      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                        ℹ️ Les données QA/QC sont <strong>optionnelles</strong> mais recommandées pour les lots BGM.
                        Cliquez sur "Charger Template" pour ajouter les standards de référence.
                      </Alert>
                    )}
                    
                    {showQaqcForm && (
                      <Box>
                        <Alert severity="success" sx={{ mb: 2, fontSize: '0.85rem' }}>
                          ✅ Template chargé. Remplissez les valeurs manquantes (Réf Client, Valeurs).
                        </Alert>
                        
                        {/* BIS SAMPLE SELECTION - SIMPLIFIED TO 2 BIS */}
                        <Box sx={{ mb: 3, p: 2, backgroundColor: alpha('#2196f3', 0.08), border: `2px solid ${theme.palette.info.main}`, borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                            📊 Sélectionner le 1er BIS (pour la ligne 1) :
                          </Typography>
                          
                          <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                            <Grid container spacing={1}>
                              {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                <Grid item xs={12} sm={6} md={4} key={sample.id}>
                                  <Paper sx={{ p: 1, backgroundColor: '#fff' }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={sample.id === firstBISSampleId}
                                          onChange={(e) => handleFirstBISChange(sample.id, e.target.checked)}
                                          disabled={sample.id === secondBISSampleId}
                                          size="small"
                                          sx={{ 
                                            color: '#1976d2',
                                            '&.Mui-checked': { color: '#1976d2' }
                                          }}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {sample.sample_id}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                            Position {idx + 1}/{((selectedSample as any)?.batchSamples || []).length}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                    {sample.id === firstBISSampleId && (
                                      <Chip label="1er BIS" size="small" color="primary" sx={{ ml: 1, fontSize: '0.6rem' }} />
                                    )}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#ff9800' }}>
                            📊 Sélectionner le 2ème BIS (pour la ligne 2) :
                          </Typography>
                          
                          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                            <Grid container spacing={1}>
                              {((selectedSample as any)?.batchSamples || []).map((sample: any, idx: number) => (
                                <Grid item xs={12} sm={6} md={4} key={`second-${sample.id}`}>
                                  <Paper sx={{ p: 1, backgroundColor: '#fff' }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={sample.id === secondBISSampleId}
                                          onChange={(e) => handleSecondBISChange(sample.id, e.target.checked)}
                                          disabled={sample.id === firstBISSampleId}
                                          size="small"
                                          sx={{ 
                                            color: '#ff9800',
                                            '&.Mui-checked': { color: '#ff9800' }
                                          }}
                                        />
                                      }
                                      label={
                                        <Box>
                                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {sample.sample_id}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                                            Position {idx + 1}/{((selectedSample as any)?.batchSamples || []).length}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                    {sample.id === secondBISSampleId && (
                                      <Chip label="2ème BIS" size="small" color="warning" sx={{ ml: 1, fontSize: '0.6rem' }} />
                                    )}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                          
                          <Alert severity="info" sx={{ mt: 2, fontSize: '0.7rem' }}>
                            💡 <strong>Astuce :</strong> Le 1er BIS utilise sa propre référence client. Le 2ème BIS utilise automatiquement <strong>BIS(1er BIS référence)</strong>.
                          </Alert>
                        </Box>
                        
                        {/* EDITABLE QA/QC TABLE - 3-ROW HEADER matching your image EXACTLY */}
                        <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              {/* HEADER ROW 1: METHOD */}
                              <TableRow>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '150px' }}>
                                  METHOD
                                </TableCell>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '120px' }}>
                                  {/* Empty column between METHOD and data */}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/002
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/035
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/001
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/038
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/038
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555', minWidth: '70px' }}>
                                  MO/ANA/038
                                </TableCell>
                                <TableCell rowSpan={3} sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555', minWidth: '80px' }}>
                                  Actions
                                </TableCell>
                              </TableRow>
                              
                              {/* HEADER ROW 2: LIMITE DETECTION */}
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,01</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>200</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>0,05</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#b8cce4', fontSize: '0.65rem', textAlign: 'center', border: '1px solid #555' }}>1</TableCell>
                              </TableRow>
                              
                              {/* HEADER ROW 3: Customers-Ref, Lab-Ref, Element names + units */}
                              <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Ag<br/><span style={{ fontSize: '0.6rem' }}>(g/t)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Cu<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Fe<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Pb<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Zn<br/><span style={{ fontSize: '0.6rem' }}>(%)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Ag_FA<br/><span style={{ fontSize: '0.6rem' }}>(g/t)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Au_FA<br/><span style={{ fontSize: '0.6rem' }}>(g/t)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Mo_ICP<br/><span style={{ fontSize: '0.6rem' }}>(ppm)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  As_ICP<br/><span style={{ fontSize: '0.6rem' }}>(ppm)</span>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#dbe5f1', fontSize: '0.7rem', textAlign: 'center', border: '1px solid #555' }}>
                                  Sn_ICP<br/><span style={{ fontSize: '0.6rem' }}>(ppm)</span>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {qaqcStandards.map((standard, idx) => (
                                <TableRow key={idx} sx={{ 
                                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                                  border: '1px solid #ddd'
                                }}>
                                  {/* Customers-Ref (editable) */}
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      value={standard.customers_ref}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].customers_ref = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder="Ex: BMX25120250"
                                      sx={{ width: '130px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Lab-Ref (editable) */}
                                  <TableCell>
                                    <TextField
                                      size="small"
                                      value={standard.lab_ref}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].lab_ref = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder="Ex: R-9348-25-25"
                                      sx={{ width: '120px' }}
                                      inputProps={{ style: { fontSize: '0.7rem' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Ag - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Ag}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Ag = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Cu - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Cu}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Cu = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Fe - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Fe}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Fe = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Pb - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Pb}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Pb = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Zn - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Zn}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Zn = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Ag_FA - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Ag_FA}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Ag_FA = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Au_FA - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Au_FA}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Au_FA = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Mo_ICP - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Mo_ICP}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Mo_ICP = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* As_ICP - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.As_ICP}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].As_ICP = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Sn_ICP - ALWAYS EDITABLE */}
                                  <TableCell sx={{ border: '1px solid #ddd' }}>
                                    <TextField
                                      size="small"
                                      value={standard.Sn_ICP}
                                      onChange={(e) => {
                                        const updated = [...qaqcStandards];
                                        updated[idx].Sn_ICP = e.target.value;
                                        setQaqcStandards(updated);
                                      }}
                                      placeholder=""
                                      sx={{ width: '70px' }}
                                      inputProps={{ style: { fontSize: '0.7rem', textAlign: 'center' } }}
                                    />
                                  </TableCell>
                                  
                                  {/* Actions */}
                                  <TableCell>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setQaqcStandards(qaqcStandards.filter((_, i) => i !== idx));
                                      }}
                                      title="Supprimer cette ligne"
                                    >
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        
                        <Box mt={1} display="flex" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            💡 Ces données seront incluses dans le certificat final
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Cancel />}
                            onClick={() => {
                              setShowQaqcForm(false);
                              setQaqcStandards([]);
                            }}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Annuler QA/QC
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Validation Decision Section */}
              <Card sx={{ mt: 2, backgroundColor: '#f0f8ff' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#1976d2' }}>
                    📝 Décision de Validation {(selectedSample as any)?.isBatchValidation ? 'du Lot' : ''}
                  </Typography>
                  
                  {/* Validation Decision Radio Buttons */}
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      value={validationDecision}
                      onChange={(e) => setValidationDecision(e.target.value as 'APPROVE' | 'REJECT' | 'REQUEST_CORRECTIONS')}
                      row
                    >
                      <MuiFormControlLabel
                        value="APPROVE"
                        control={<Radio color="success" />} 
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircle color="success" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Approuver {(selectedSample as any)?.isBatchValidation ? 'le Lot' : "l'Échantillon"}
                            </Typography>
                          </Box>
                        }
                      />
                      <MuiFormControlLabel
                        value="REJECT"
                        control={<Radio color="error" />}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Cancel color="error" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">
                              Rejeter {(selectedSample as any)?.isBatchValidation ? 'le Lot' : "l'Échantillon"}
                            </Typography>
                          </Box>
                        }
                      />
                      <MuiFormControlLabel
                        value="REQUEST_CORRECTIONS"
                        control={<Radio color="warning" />}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Warning color="warning" sx={{ fontSize: '1rem' }} />
                            <Typography variant="body2">Demander Corrections</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Notes de validation (OBLIGATOIRES)"
                    value={validationNotes}
                    onChange={(e) => setValidationNotes(e.target.value)}
                    placeholder="Commentaires obligatoires sur la qualité des analyses ICP, conformité des résultats, actions correctives si nécessaire..."
                    required
                    error={!validationNotes.trim()}
                    helperText={!validationNotes.trim() ? "Les notes de validation sont obligatoires pour valider un échantillon" : "Notes sauvegardées automatiquement"}
                    sx={{ 
                      mt: 2,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: !validationNotes.trim() ? '#d32f2f' : '#1976d2'
                        }
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 2, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          zIndex: 1000
        }}>
          <Button 
            onClick={() => setValidationDialog(false)} 
            startIcon={<Cancel />}
            sx={{ fontSize: '0.8rem' }}
          >
            Annuler
          </Button>
          
          {/* Enable validation button for both individual samples and batch samples */}
          {(selectedSample?.metadonnees?.icp_analysis_form || 
            selectedSample?.metadonnees?.icp_batch_analysis_summary ||
            (selectedSample as any)?.isBatchValidation) && (
            <Button 
              variant="contained" 
              onClick={handleCompleteValidation}
              startIcon={<Save />}
              disabled={!validationNotes.trim()}
              color={validationDecision === 'APPROVE' ? 'success' : 'error'}
              sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
              title={!validationNotes.trim() ? "Veuillez saisir des notes de validation pour activer ce bouton" : ""}
            >
              {(selectedSample as any)?.isBatchValidation 
                ? (validationDecision === 'APPROVE' ? 'Approuver le Lot' : 'Rejeter le Lot')
                : (validationDecision === 'APPROVE' ? 'Approuver l\'Échantillon' : 
                   (validationDecision === 'REJECT' ? 'Rejeter l\'Échantillon' : 'Demander Corrections'))
              }
              {!validationNotes.trim() && (
                <Typography variant="caption" sx={{ ml: 1, opacity: 0.7, fontSize: '0.7rem' }}>
                  (Notes requises)
                </Typography>
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default ICPSupervisorDashboard; 